/**
 * A second document that rides along on the receipt's print job.
 *
 * `IsPrintSalesManSummary` used to open its own window straight after the
 * receipt's, which meant two tabs and two print dialogs per sale — and on a
 * desktop browser the second dialog could open in a tab the cashier had
 * already left, freezing the till behind it (`window.print()` is synchronous
 * for the whole renderer). Appending the extra sheet to the receipt document
 * on a forced page break gives one tab, one dialog, one cut between the two
 * papers.
 *
 * A section is self-contained: its CSS is scoped under its own root class so
 * it can be dropped into any receipt document (thermal, A4, custom design)
 * without inheriting or leaking rules.
 */
export interface PrintSection {
  /** CSS scoped under the section's root class — no `body`/`@page` rules. */
  css: string;
  /** A single root element carrying that class. */
  html: string;
}

const SECTION_WRAP_CSS =
  '.matix-print-section { break-before: page; page-break-before: always; }';

/**
 * Insert `section` at the end of a complete HTML document, on its own page.
 * Falls back to plain concatenation when the document has no `</head>` /
 * `</body>` to anchor on (never the case for our own builders).
 */
export function appendPrintSection(docHtml: string, section: PrintSection): string {
  const style = `<style>${SECTION_WRAP_CSS}\n${section.css}</style>`;
  const body = `<div class="matix-print-section">${section.html}</div>`;

  const headAt = docHtml.search(/<\/head>/i);
  const withStyle = headAt >= 0
    ? docHtml.slice(0, headAt) + style + docHtml.slice(headAt)
    : style + docHtml;

  const bodyAt = withStyle.search(/<\/body>/i);
  return bodyAt >= 0
    ? withStyle.slice(0, bodyAt) + body + withStyle.slice(bodyAt)
    : withStyle + body;
}

/**
 * Wrap a section as a standalone thermal document, for the one path that
 * cannot carry it along — the A4 report invoice, which is a PDF the report
 * server produced. Same 72 mm band and inner padding as the receipt.
 */
export function sectionDocument(
  section: PrintSection,
  opts: { title: string; lang: 'ar' | 'en' },
): string {
  const dir = opts.lang === 'ar' ? 'rtl' : 'ltr';
  return `<!DOCTYPE html>
<html lang="${opts.lang}" dir="${dir}">
<head>
<meta charset="utf-8">
<title>${opts.title.replace(/[<>&]/g, '')}</title>
<style>
@page { size: 72mm auto; margin: 0; }
* { box-sizing: border-box; }
html, body {
  margin: 0; padding: 0; width: 72mm; background: white; color: #000;
  font-family: Tahoma, "Geeza Pro", "Noto Sans Arabic", Arial, sans-serif;
  font-size: 11px; line-height: 1.35;
}
body { padding: 4mm 3mm; }
${section.css}
</style>
</head>
<body>
${section.html}
</body>
</html>`;
}
